<template>
  <div class="project-tree">
    <div class="tree-header">
      <span class="tree-title">项目列表</span>
      <div class="header-actions">
        <el-button
          v-if="selectedApis.size > 0"
          type="danger"
          size="small"
          @click="handleBatchDelete"
        >
          <el-icon><Delete /></el-icon>
          删除({{ selectedApis.size }})
        </el-button>
        <el-dropdown @command="handleHeaderCommand">
          <el-button type="primary" size="small" text>
            <el-icon><More /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="add-project">
                <el-icon><Plus /></el-icon>新建项目
              </el-dropdown-item>
              <el-dropdown-item command="import" divided>
                <el-icon><Download /></el-icon>导入 Postman
              </el-dropdown-item>
              <el-dropdown-item command="import-openapi">
                <el-icon><Download /></el-icon>导入 OpenAPI
              </el-dropdown-item>
              <el-dropdown-item command="export-all" divided>
                <el-icon><Upload /></el-icon>导出全部为 Postman
              </el-dropdown-item>
              <el-dropdown-item command="export-all-openapi">
                <el-icon><Upload /></el-icon>导出全部为 OpenAPI
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
    
    <div class="tree-content">
      <el-tree
        ref="treeRef"
        :data="treeData"
        node-key="id"
        default-expand-all
        :expand-on-click-node="false"
        draggable
        :allow-drag="allowDrag"
        :allow-drop="allowDrop"
        @node-drag-start="handleDragStart"
        @node-drag-enter="handleDragEnter"
        @node-drag-leave="handleDragLeave"
        @node-drag-over="handleDragOver"
        @node-drag-end="handleDragEnd"
        @node-drop="handleDrop"
        :props="{ label: 'name', children: 'children' }"
        @node-click="handleNodeClick"
      >
        <template #default="{ data }">
          <div class="tree-node" :class="{
            'is-api': data.type === 'api',
            'is-selected': (data.type === 'api' && currentApi && currentApi.id === data.id) ||
                          (data.type !== 'api' && selectedNode && selectedNode.id === data.id)
          }">
            <!-- 选择框（仅接口显示） -->
            <input
              v-if="data.type === 'api'"
              type="checkbox"
              :checked="selectedApis.has(data.id)"
              class="api-checkbox"
              @click.stop
              @change="(e) => handleSelectChange(e.target.checked, data)"
            />
            
            <!-- 图标 -->
            <el-icon v-if="data.type === 'project'" class="node-icon"><Folder /></el-icon>
            <el-icon v-else-if="data.type === 'folder'" class="node-icon"><FolderOpened /></el-icon>
            <el-tag 
              v-else-if="data.type === 'api'"
              :type="getMethodType(data.method)" 
              size="small"
              class="method-tag"
            >
              {{ data.method }}
            </el-tag>
            
            <!-- 名称 -->
            <span class="node-name" :title="data.name">{{ data.name }}</span>
            
            <!-- 操作按钮 -->
            <div class="node-actions" @click.stop>
              <el-dropdown v-if="data.type !== 'api'" @command="handleCommand($event, data)">
                <el-button type="primary" size="small" text>
                  <el-icon><More /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="add-api">
                      <el-icon><Plus /></el-icon>添加接口
                    </el-dropdown-item>
                    <el-dropdown-item command="add-folder">
                      <el-icon><FolderAdd /></el-icon>添加文件夹
                    </el-dropdown-item>
                    <el-dropdown-item command="edit">
                      <el-icon><Edit /></el-icon>{{ data.type === 'project' ? '编辑项目' : '编辑文件夹' }}
                    </el-dropdown-item>
                    <el-dropdown-item command="export" divided>
                      <el-icon><Upload /></el-icon>导出为 Postman
                    </el-dropdown-item>
                    <el-dropdown-item command="export-openapi">
                      <el-icon><Upload /></el-icon>导出为 OpenAPI
                    </el-dropdown-item>
                    <el-dropdown-item v-if="data.type === 'project'" command="import" divided>
                      <el-icon><Download /></el-icon>导入 Postman
                    </el-dropdown-item>
                    <el-dropdown-item v-if="data.type === 'project'" command="import-openapi">
                      <el-icon><Download /></el-icon>导入 OpenAPI
                    </el-dropdown-item>
                    <el-dropdown-item command="delete" divided>
                      <el-icon><Delete /></el-icon>删除
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              
              <el-dropdown v-else @command="handleApiCommand($event, data)">
                <el-button type="primary" size="small" text>
                  <el-icon><More /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="export">
                      <el-icon><Upload /></el-icon>导出接口为 Postman
                    </el-dropdown-item>
                    <el-dropdown-item command="export-openapi">
                      <el-icon><Upload /></el-icon>导出接口为 OpenAPI
                    </el-dropdown-item>
                    <el-dropdown-item command="delete" divided>
                      <el-icon><Delete /></el-icon>删除接口
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </template>
      </el-tree>
      
      <el-empty v-if="projects.length === 0" description="暂无项目">
        <el-button type="primary" @click="$emit('add-project')">
          创建项目
        </el-button>
      </el-empty>
    </div>

    <!-- 编辑对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      :title="editForm.type === 'project' ? '编辑项目' : '编辑文件夹'"
      width="400px"
    >
      <el-form ref="editFormRef" :model="editForm" :rules="editRules" label-position="top">
        <el-form-item label="名称" prop="name">
          <el-input v-model="editForm.name" placeholder="请输入名称" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input
            v-model="editForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入描述（可选）"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveEdit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessageBox, ElMessage } from 'element-plus'

// eslint-disable-next-line no-undef
const props = defineProps({
  projects: {
    type: Array,
    default: () => []
  },
  currentApi: {
    type: Object,
    default: null
  }
})

// eslint-disable-next-line no-undef
const emit = defineEmits([
  'select-api', 'select-node', 'add-project', 'add-folder', 'add-api',
  'delete-node', 'edit-node', 'export-node', 'import-project',
  'import-all', 'export-all', 'node-sort',
  'import-openapi-all', 'export-all-openapi',
  'import-openapi-project', 'export-node-openapi'
])

const treeRef = ref(null)
const selectedApis = ref(new Set())
const selectedNode = ref(null)
const dragNode = ref(null)

// 转换项目数据为树形数据
const treeData = computed(() => {
  return props.projects.map(project => ({
    ...project,
    children: project.children || []
  }))
})

// 处理节点点击
const handleNodeClick = (data) => {
  selectedNode.value = data
  emit('select-node', data)
  if (data.type === 'api') {
    emit('select-api', data)
  }
}

// 拖拽相关方法
const allowDrag = () => {
  // 所有节点都可以拖拽
  return true
}

const allowDrop = (draggingNode, dropNode, type) => {
  // 不允许拖拽到接口节点内（只能平级排序）
  if (dropNode.data.type === 'api' && type === 'inner') {
    return false
  }
  // 接口只能和接口排序，文件夹/项目只能和同类排序
  if (draggingNode.data.type === 'api' && dropNode.data.type !== 'api') {
    return false
  }
  if (draggingNode.data.type !== 'api' && dropNode.data.type === 'api') {
    return false
  }
  return true
}

const handleDragStart = (node) => {
  dragNode.value = node
}

const handleDragEnter = () => {
  // 可以添加视觉反馈
}

const handleDragLeave = () => {
  // 可以移除视觉反馈
}

const handleDragOver = () => {
  // 拖拽经过时
}

const handleDragEnd = () => {
  dragNode.value = null
}

const handleDrop = (draggingNode, dropNode, dropType) => {
  // 触发排序事件
  emit('node-sort', {
    dragNode: draggingNode.data,
    dropNode: dropNode.data,
    dropType: dropType // 'before', 'after', 'inner'
  })
}

// 处理选择框变化
const handleSelectChange = (checked, data) => {
  if (checked) {
    selectedApis.value.add(data.id)
  } else {
    selectedApis.value.delete(data.id)
  }
}

// 批量删除
const handleBatchDelete = () => {
  if (selectedApis.value.size === 0) return

  ElMessageBox.confirm(
    `确定要删除选中的 ${selectedApis.value.size} 个接口吗？`,
    '批量删除',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(() => {
    const ids = Array.from(selectedApis.value)
    emit('delete-node', { type: 'batch', ids })
    selectedApis.value.clear()
    ElMessage.success('删除成功')
  }).catch(() => {
    // 取消操作，不做处理
  })
}

// 处理顶部下拉菜单命令
const handleHeaderCommand = (command) => {
  switch (command) {
    case 'add-project':
      emit('add-project')
      break
    case 'import':
      emit('import-all')
      break
    case 'import-openapi':
      emit('import-openapi-all')
      break
    case 'export-all':
      emit('export-all')
      break
    case 'export-all-openapi':
      emit('export-all-openapi')
      break
  }
}

// 处理下拉菜单命令
const handleCommand = (command, node) => {
  switch (command) {
    case 'add-folder':
      handleAddFolder(node)
      break
    case 'add-api':
      emit('add-api', node)
      break
    case 'edit':
      handleEditNode(node)
      break
    case 'export':
      emit('export-node', node)
      break
    case 'export-openapi':
      emit('export-node-openapi', node)
      break
    case 'import':
      emit('import-project', node)
      break
    case 'import-openapi':
      emit('import-openapi-project', node)
      break
    case 'delete':
      emit('delete-node', { type: 'single', id: node.id })
      break
  }
}

// 处理接口下拉菜单命令
const handleApiCommand = (command, api) => {
  switch (command) {
    case 'export':
      emit('export-node', api)
      break
    case 'export-openapi':
      emit('export-node-openapi', api)
      break
    case 'delete':
      emit('delete-node', { type: 'single', id: api.id })
      break
  }
}

// 添加文件夹
const handleAddFolder = (parentNode) => {
  ElMessageBox.prompt('请输入文件夹名称', '新建文件夹', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
  }).then(({ value }) => {
    if (value) {
      emit('add-folder', { parentId: parentNode.id, name: value })
    }
  }).catch(() => {
    // 取消操作，不做处理
  })
}

// 编辑对话框状态
const editDialogVisible = ref(false)
const editForm = ref({
  id: '',
  name: '',
  description: '',
  type: ''
})
const editFormRef = ref(null)
const editRules = {
  name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
}

// 编辑节点（项目或文件夹）
const handleEditNode = (node) => {
  editForm.value = {
    id: node.id,
    name: node.name,
    description: node.description || '',
    type: node.type
  }
  editDialogVisible.value = true
}

// 保存编辑
const saveEdit = async () => {
  if (!editFormRef.value) return

  await editFormRef.value.validate((valid) => {
    if (valid) {
      emit('edit-node', {
        id: editForm.value.id,
        name: editForm.value.name,
        description: editForm.value.description
      })
      editDialogVisible.value = false
    }
  })
}

// 获取方法类型
const getMethodType = (method) => {
  const types = {
    'GET': 'success',
    'POST': 'primary',
    'PUT': 'warning',
    'DELETE': 'danger',
    'PATCH': 'info'
  }
  return types[method?.toUpperCase()] || 'info'
}
</script>

<style scoped>
.project-tree {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
}

.tree-title {
  font-weight: 600;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.tree-header {
  padding: 8px 0;
}
.tree-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.tree-node {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 4px 0;
}

.tree-node.is-api {
  padding-left: 8px;
}

.tree-node.is-selected {
  background-color: #ecf5ff;
}

.node-icon {
  margin-right: 6px;
  color: #909399;
}

.method-tag {
  margin-right: 6px;
  min-width: 50px;
  text-align: center;
}

.node-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.node-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.tree-node:hover .node-actions {
  opacity: 1;
}

:deep(.el-tree-node__content) {
  height: auto;
  padding: 2px 0;
}

/* 拖拽时的样式 */
:deep(.el-tree-node.is-drop-inner > .el-tree-node__content) {
  background-color: #409eff;
  color: #fff;
}

:deep(.el-tree-node.is-drop-before > .el-tree-node__content) {
  border-top: 2px solid #409eff;
}

:deep(.el-tree-node.is-drop-after > .el-tree-node__content) {
  border-bottom: 2px solid #409eff;
}

:deep(.el-tree-node.is-dragging > .el-tree-node__content) {
  opacity: 0.5;
}

.api-checkbox {
  margin-right: 6px;
  width: 14px;
  height: 14px;
  cursor: pointer;
}

:deep(.el-checkbox) {
  margin-right: 6px;
}
</style>
